import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  CheckCircle2, 
  Clock, 
  ArrowRight, 
  Trophy,
  Target,
  Activity,
  Brain,
  Heart,
  Users,
  Calendar,
  TrendingUp,
  Star
} from 'lucide-react';
import PageHeader from '@/components/ui/page-header';
import AdminJsonToggle from './AdminJsonToggle';
import type { Module } from '@/types';

interface DashboardSection {
  id: string;
  type: 'progress' | 'stats' | 'actions' | 'insights' | 'modules';
  title: string;
  description?: string;
  content: any;
  order: number;
}

interface DashboardModuleData {
  welcomeMessage?: string;
  sections: DashboardSection[];
  showProgress?: boolean;
  showStats?: boolean;
  primaryAction?: {
    label: string;
    targetModule?: string;
    externalUrl?: string;
  };
}

interface DynamicDashboardProps {
  module: Module;
}

// Icon mapping for different content types
const iconMap: Record<string, React.ReactNode> = {
  completed: <CheckCircle2 className="h-5 w-5 text-green-600" />,
  'in-progress': <Clock className="h-5 w-5 text-blue-600" />,
  'not-started': <Target className="h-5 w-5 text-gray-400" />,
  trophy: <Trophy className="h-5 w-5 text-yellow-600" />,
  activity: <Activity className="h-5 w-5 text-purple-600" />,
  brain: <Brain className="h-5 w-5 text-indigo-600" />,
  heart: <Heart className="h-5 w-5 text-red-600" />,
  users: <Users className="h-5 w-5 text-teal-600" />,
  calendar: <Calendar className="h-5 w-5 text-orange-600" />,
  trending: <TrendingUp className="h-5 w-5 text-green-600" />,
  star: <Star className="h-5 w-5 text-yellow-500" />,
};

export default function DynamicDashboard({ module }: DynamicDashboardProps) {
  const navigate = useNavigate();
  
  // Parse module data for dashboard configuration
  let dashboardData: DashboardModuleData = { sections: [] };
  
  try {
    if (module.data) {
      dashboardData = JSON.parse(module.data);
    }
  } catch (error) {
    console.error('Failed to parse dashboard module data:', error, module.data);
  }
  
  console.log('Dashboard module:', module);
  console.log('Dashboard data:', dashboardData);
  
  const { welcomeMessage, sections = [] } = dashboardData;
  
  // Sort sections by order
  const sortedSections = [...sections].sort((a, b) => a.order - b.order);
  
  const renderProgressSection = (section: DashboardSection) => {
    const items = section.content.items || [];
    
    return (
      <Card key={section.id}>
        <CardHeader>
          <CardTitle>{section.title}</CardTitle>
          {section.description && <CardDescription>{section.description}</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-4">
          {items.map((item: any) => (
            <div key={item.id} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {iconMap[item.icon] || iconMap[item.status]}
                  <span className="font-medium">{item.label}</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {item.progress}%
                </span>
              </div>
              <Progress value={item.progress} className="h-2" />
              {item.sublabel && (
                <p className="text-xs text-muted-foreground">{item.sublabel}</p>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };
  
  const renderStatsSection = (section: DashboardSection) => {
    const stats = section.content.stats || [];
    
    return (
      <Card key={section.id}>
        <CardHeader>
          <CardTitle>{section.title}</CardTitle>
          {section.description && <CardDescription>{section.description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {stats.map((stat: any) => (
              <div key={stat.id} className="space-y-1">
                <div className="flex items-center gap-2">
                  {iconMap[stat.icon]}
                  <span className="text-2xl font-bold">{stat.value}</span>
                </div>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };
  
  const renderActionsSection = (section: DashboardSection) => {
    const actions = section.content.actions || [];
    
    return (
      <Card key={section.id}>
        <CardHeader>
          <CardTitle>{section.title}</CardTitle>
          {section.description && <CardDescription>{section.description}</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-3">
          {actions.map((action: any) => (
            <Button
              key={action.id}
              variant={action.variant || 'outline'}
              className="w-full justify-between"
              onClick={() => {
                if (action.targetModule) {
                  navigate('/module');
                } else if (action.route) {
                  navigate(action.route);
                } else if (action.externalUrl) {
                  window.open(action.externalUrl, '_blank');
                }
              }}
              disabled={action.disabled}
            >
              <div className="flex items-center gap-2">
                {iconMap[action.icon]}
                <span>{action.label}</span>
              </div>
              <ArrowRight className="h-4 w-4" />
            </Button>
          ))}
        </CardContent>
      </Card>
    );
  };
  
  const renderInsightsSection = (section: DashboardSection) => {
    const insights = section.content.insights || [];
    
    return (
      <Card key={section.id}>
        <CardHeader>
          <CardTitle>{section.title}</CardTitle>
          {section.description && <CardDescription>{section.description}</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-3">
          {insights.map((insight: any) => (
            <div key={insight.id} className="flex gap-3 p-3 bg-secondary/50 rounded-lg">
              <div className="flex-shrink-0 mt-0.5">
                {iconMap[insight.icon] || <Star className="h-5 w-5 text-primary" />}
              </div>
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium">{insight.title}</p>
                <p className="text-sm text-muted-foreground">{insight.description}</p>
                {insight.action && (
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs"
                    onClick={() => navigate(insight.action.route)}
                  >
                    {insight.action.label} →
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };
  
  const renderModulesSection = (section: DashboardSection) => {
    const modules = section.content.modules || [];
    
    return (
      <Card key={section.id}>
        <CardHeader>
          <CardTitle>{section.title}</CardTitle>
          {section.description && <CardDescription>{section.description}</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-3">
          {modules.map((mod: any) => (
            <div
              key={mod.id}
              className={cn(
                "p-4 rounded-lg border cursor-pointer transition-colors",
                mod.status === 'completed' ? "bg-green-50 border-green-200" :
                mod.status === 'in-progress' ? "bg-blue-50 border-blue-200" :
                "bg-gray-50 border-gray-200 hover:bg-gray-100"
              )}
              onClick={() => {
                if (mod.status !== 'completed' && !mod.locked) {
                  navigate('/module');
                }
              }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {iconMap[mod.status]}
                    <h4 className="font-medium">{mod.title}</h4>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{mod.description}</p>
                  {mod.progress !== undefined && (
                    <div className="mt-2">
                      <Progress value={mod.progress} className="h-1.5" />
                      <p className="text-xs text-muted-foreground mt-1">
                        {mod.progress}% complete
                      </p>
                    </div>
                  )}
                </div>
                {mod.status !== 'completed' && !mod.locked && (
                  <ArrowRight className="h-5 w-5 text-muted-foreground flex-shrink-0 ml-4" />
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };
  
  const renderSection = (section: DashboardSection) => {
    switch (section.type) {
      case 'progress':
        return renderProgressSection(section);
      case 'stats':
        return renderStatsSection(section);
      case 'actions':
        return renderActionsSection(section);
      case 'insights':
        return renderInsightsSection(section);
      case 'modules':
        return renderModulesSection(section);
      default:
        return null;
    }
  };
  
  return (
    <div className="min-h-screen bg-white safe-area-inset">
      <AdminJsonToggle 
        data={{
          module,
          dashboardData,
          sections,
          primaryAction: dashboardData.primaryAction
        }}
        label="Dashboard Module Data"
      />
      <PageHeader 
        title={module.name || "Dashboard"}
        variant="title-only"
      />
      
      <ScrollArea className="h-[calc(100vh-80px)]">
        <div className="px-4 py-6 space-y-6">
          {welcomeMessage && (
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="pt-6">
                <p className="text-sm">{welcomeMessage}</p>
              </CardContent>
            </Card>
          )}
          
          {sortedSections.length > 0 ? (
            sortedSections.map(section => renderSection(section))
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">Welcome to Your Dashboard</h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    Your personalized dashboard will appear here once configured.
                  </p>
                  <Button onClick={() => navigate('/updates')}>
                    Go to Updates
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          
          {dashboardData.primaryAction && (
            <div className="pt-4">
              <Button
                className="w-full"
                size="lg"
                onClick={() => {
                  if (dashboardData.primaryAction?.targetModule) {
                    navigate('/module');
                  } else if (dashboardData.primaryAction?.externalUrl) {
                    window.open(dashboardData.primaryAction.externalUrl, '_blank');
                  }
                }}
              >
                {dashboardData.primaryAction.label}
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// Helper function for conditional classes
function cn(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}